import { useMemo } from 'react';
import dayjs from 'dayjs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Serializer, UISerializableFundingPaymentRecord } from '@drift/common';
import {
	MarketPerformanceDataV2,
	PnLTimeSeriesDataPoint,
} from 'src/@types/performance';
import { PortfolioPeriod } from 'src/components/PortfolioPage/types';
import { PORTFOLIO_DAYS_BY_PERIOD } from 'src/components/PortfolioPage/consts';
import DataApiClient from 'src/utils/DataApiClient';
import {
	addInitialZero,
	addZeroes,
	applyTimeFilter,
	cumulatizePnlPoints,
} from 'src/utils/pnlChart';
import { QueryDisabledError } from 'src/utils/error/QueryDisabledError';
import { createQueryKey } from 'src/utils/query/createQueryKey';
import useLatchedValue from 'src/hooks/utils/useLatchedValue';

interface Params {
	userAccount: string | undefined;
	period: PortfolioPeriod;
	marketFilter: number | 'all_markets';
}

/**
 * Returns the funding payments historical data for a given user account and period.
 * The data returned will start from 00:00 UTC of the starting day of the period.
 */
export function useFundingPaymentsPerformance({
	userAccount,
	period: periodFromProps,
	marketFilter,
}: Params) {
	const disabled = !userAccount;

	const { data: allFundingRecords, ...rest } = useQuery({
		queryKey: createQueryKey(
			'fundingPaymentsPerformance',
			userAccount,
			periodFromProps
		),
		queryFn: async () => {
			if (disabled) {
				throw new QueryDisabledError();
			}

			const days = PORTFOLIO_DAYS_BY_PERIOD[periodFromProps];
			const endDate = dayjs();
			const startDate = endDate.subtract(days, 'day');

			const result = await DataApiClient.fetchLongTermData<
				Record<string, unknown>
			>('funding-payments', {
				userAccount,
				startDate,
				endDate,
			});

			if (!result.success || !result.records.length) {
				return [];
			}

			return result.records.map(
				(record) =>
					Serializer.Deserialize.UIFundingPayment(
						record as Parameters<
							typeof Serializer.Deserialize.UIFundingPayment
						>[0]
					) as UISerializableFundingPaymentRecord
			);
		},
		staleTime: 60_000,
		enabled: !disabled,
		placeholderData: keepPreviousData,
	});

	const period = useLatchedValue({
		value: periodFromProps,
		isLatched: rest.isPlaceholderData,
	});

	const filteredFundingRecords = useMemo((): MarketPerformanceDataV2[] => {
		if (!allFundingRecords?.length) {
			return [];
		}

		// Filter by date range based on period.
		// PORTFOLIO_DAYS_BY_PERIOD returns 100 for 90d.
		const days = period === '90d' ? 90 : PORTFOLIO_DAYS_BY_PERIOD[period];
		const cutoffTs = dayjs().startOf('day').subtract(days, 'day').unix();

		const periodFilteredRecords = allFundingRecords.filter(
			(record) => record.ts.toNumber() >= cutoffTs
		);

		const marketFilteredRecords =
			marketFilter !== 'all_markets'
				? periodFilteredRecords.filter(
						(record) => record.marketIndex === marketFilter
					)
				: periodFilteredRecords;

		return [
			{
				marketFilter,
				performance: buildPerformanceData(marketFilteredRecords),
			},
		];
	}, [allFundingRecords, marketFilter, period]);

	return {
		data: filteredFundingRecords,
		...rest,
	};
}

function buildPerformanceData(
	fundingRecords: UISerializableFundingPaymentRecord[]
) {
	// Sort by timestamp ascending
	const sortedRecords = [...fundingRecords].sort(
		(a, b) => a.ts.toNumber() - b.ts.toNumber()
	);

	// Transform to PnL data points
	const allDataPoints: PnLTimeSeriesDataPoint[] = sortedRecords.map(
		(record) => ({
			date: new Date(record.ts.toNumber() * 1000),
			totalPnl: record.fundingPayment.toNum(),
		})
	);

	const points90d = addZeroes(
		addInitialZero(cumulatizePnlPoints(allDataPoints, 'totalPnl'), '90d'),
		'totalPnl'
	);

	const points30d = addZeroes(
		cumulatizePnlPoints(
			addInitialZero(applyTimeFilter(allDataPoints, '30d'), '30d'),
			'totalPnl'
		),
		'totalPnl'
	);

	const points7d = addZeroes(
		cumulatizePnlPoints(
			addInitialZero(applyTimeFilter(allDataPoints, '7d'), '7d'),
			'totalPnl'
		),
		'totalPnl'
	);

	const points24h = addZeroes(
		cumulatizePnlPoints(
			addInitialZero(applyTimeFilter(allDataPoints, '24h'), '24h'),
			'totalPnl'
		),
		'totalPnl'
	);

	return {
		'24h': points24h,
		'7d': points7d,
		'30d': points30d,
		all: points90d,
	};
}
